// Minimal pub-sub store — the whole app re-renders from this on every
// change (small app, simplicity over a diffing layer).
const listeners = new Set();

const FX = { USDC: 1, USDT: 0.9991, EURC: 1.0863 };
const CHAINS = {
  USDC: ['Ethereum · ERC-20', 'Polygon · ERC-20', 'Base · ERC-20'],
  EURC: ['Ethereum · ERC-20', 'Polygon · ERC-20'],
  USDT: ['Tron · TRC-20', 'Ethereum · ERC-20']
};
const NET = { USDC: 'Ethereum · ERC-20', EURC: 'Ethereum · ERC-20', USDT: 'Tron · TRC-20' };
const G20 = ['USD', 'EUR', 'GBP', 'JPY', 'CNY', 'INR', 'AUD', 'CAD', 'BRL', 'MXN', 'KRW', 'IDR', 'SAR', 'ZAR', 'TRY', 'RUB', 'ARS'];
// Static — live rates/symbols (frankfurter.dev) are out of scope per
// feature-ui.md's "Extra changes" (backend, explicitly deferred). Distinct
// prefixes even where real-world usage overlaps (e.g. AUD/CAD both as "$"
// locally) so a reporting-currency switch is never ambiguous with USD.
const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CNY: '¥', INR: '₹', AUD: 'A$', CAD: 'C$',
  BRL: 'R$', MXN: 'MX$', KRW: '₩', IDR: 'Rp', SAR: 'SR', ZAR: 'R', TRY: '₺', RUB: '₽', ARS: 'AR$'
};
// circleUserRound is the default/first avatar (assigned to every new
// profile — see submitOpenAccount), the animal set is only ever reached by
// changing it afterward in Settings.
const AVATARS = ['circleUserRound', 'cat', 'dog', 'bird', 'fish', 'rabbit', 'turtle', 'squirrel', 'bug'];

export const CONFIG = { FX, CHAINS, NET, G20, AVATARS };

// The Landing screen itself decides whether to show "welcome back" (a
// passcode prompt) or the new-account form, based on whether any profiles
// exist on this device — so the initial screen is always 'landing'; a
// reload must never skip straight past a passcode prompt into Home.
function loadProfiles() {
  try {
    const raw = localStorage.getItem('tynti_profiles');
    if (raw) {
      const list = JSON.parse(raw);
      // Pre-vault profiles (old passcodeHash/passcodeSalt shape, no
      // `vault`) can't be migrated without the user's original PIN in
      // hand to re-derive a wrap from — dropped deliberately rather than
      // half-migrated; affected devices just re-onboard (see vault.js).
      if (Array.isArray(list)) return list.filter((p) => p && p.vault && p.vault.pin);
    }
  } catch { /* ignore corrupt storage */ }
  return [];
}
function loadActiveProfileId() {
  try { return localStorage.getItem('tynti_active_profile'); } catch { return null; }
}

// User-generated tags per Activity row, keyed by transaction id — kept
// separate from `transactions` itself since transactions are seeded fresh
// in-memory every load (no backend to persist them against), but tags a
// user types are worth keeping across reloads even so.
function loadTxnTags() {
  try {
    const raw = localStorage.getItem('tynti_txn_tags');
    if (raw) return JSON.parse(raw);
  } catch { /* ignore corrupt storage */ }
  return {};
}

// All of Settings' new preference toggles/dropdowns, bundled into one
// localStorage key rather than one key each — none of them are large or
// need independent versioning, and it keeps loadSettings/persistSettings
// (actions.js) as a single pair instead of eight near-identical ones.
function loadSettings() {
  const defaults = {
    testnetEnabled: true, // CLAUDE.md: default to testnet in local dev
    defaultStablecoin: 'USDC', defaultChain: NET.USDC
  };
  try {
    const raw = localStorage.getItem('tynti_settings');
    if (raw) return { ...defaults, ...JSON.parse(raw) };
  } catch { /* ignore corrupt storage */ }
  return defaults;
}

// 'system' means no explicit override — style.css's
// @media (prefers-color-scheme: dark) block decides. 'light'/'dark' pin it
// regardless of OS preference.
function initialTheme() {
  try {
    const t = localStorage.getItem('tynti_theme');
    if (t === 'light' || t === 'dark') return t;
  } catch { /* storage unavailable */ }
  return 'system';
}

const initialSettings = loadSettings();

let state = {
  screen: 'landing',
  ...initialSettings,
  profiles: loadProfiles(),
  activeProfileId: loadActiveProfileId(),
  landingFormOpen: false,
  landingAddingProfile: false,
  newProfileName: '', newProfileEmail: '', newProfileAnonymous: false, newProfileNameBeforeAnon: '',
  newProfileCode: '', newProfileCodeConfirm: '', agreeTerms: false, agreeSelfCustody: false,
  newProfileErrors: {}, newProfileSubmitting: false,
  lockChecking: false,
  reportingCurrency: 'USD',
  wallets: [
    { id: 'ops', name: 'Ops', owner: 'Acme Holdings Ltd', balances: { USDC: 210400, EURC: 32000 } },
    { id: 'payroll', name: 'Payroll', owner: 'Acme Holdings Ltd', balances: { USDT: 88220 } }
  ],
  transactions: [
    { id: 't1', date: 'Jul 14', dateISO: '2026-07-14', type: 'out', accountId: 'ops', desc: 'Vendor payment — Northwind Supplies', wallet: 'Ops', ccy: 'USDC', amount: -4200, hash: '8f21ac0e93b7' },
    { id: 't2', date: 'Jul 13', dateISO: '2026-07-13', type: 'in', accountId: 'ops', desc: 'Client deposit — Helios Corp', wallet: 'Ops', ccy: 'USDC', amount: 50000, hash: '2b9d4471fe0a' },
    { id: 't3', date: 'Jul 12', dateISO: '2026-07-12', type: 'out', accountId: 'payroll', desc: 'Payroll run — July cycle', wallet: 'Payroll', ccy: 'USDT', amount: -22000, hash: '9c04e6a71bd2' },
    { id: 't4', date: 'Jul 10', dateISO: '2026-07-10', type: 'fx', accountId: 'ops', desc: 'FX convert EURC → USDC', wallet: 'Ops', ccy: 'EURC', amount: -9000, hash: 'a13f7d0c2e88', fxRate: FX.EURC },
    { id: 't5', date: 'Jul 8', dateISO: '2026-07-08', type: 'out', accountId: 'ops', desc: 'Vendor payment — CloudStack Inc', wallet: 'Ops', ccy: 'USDC', amount: -1150, hash: '4d6b91af0033' },
    { id: 't6', date: 'Jul 5', dateISO: '2026-07-05', type: 'in', accountId: 'ops', desc: 'Client deposit — Aurora Retail', wallet: 'Ops', ccy: 'USDC', amount: 12000, hash: 'e0a9c5d81176' },
    { id: 't7', date: 'Jul 3', dateISO: '2026-07-03', type: 'in', accountId: 'ops', desc: 'EURC funding from EU desk', wallet: 'Ops', ccy: 'EURC', amount: 32000, hash: '7729bfa4c110' },
    { id: 't8', date: 'Jun 30', dateISO: '2026-06-30', type: 'out', accountId: 'payroll', desc: 'Payroll run — June cycle', wallet: 'Payroll', ccy: 'USDT', amount: -21500, hash: '11c2af6d9e40' },
    { id: 't9', date: 'Jun 27', dateISO: '2026-06-27', type: 'in', accountId: 'payroll', desc: 'Client deposit — Meridian Ltd', wallet: 'Payroll', ccy: 'USDT', amount: 40000, hash: '55ab3e0f9c72' },
    { id: 't10', date: 'Jun 24', dateISO: '2026-06-24', type: 'out', accountId: 'ops', desc: 'Vendor payment — DataForge', wallet: 'Ops', ccy: 'USDC', amount: -3200, hash: 'c9047fa2e1b5' },
    { id: 't11', date: 'Jun 20', dateISO: '2026-06-20', type: 'in', accountId: 'ops', desc: 'Client deposit — Northwind Supplies', wallet: 'Ops', ccy: 'USDC', amount: 30000, hash: '6f83d2a9c004' },
    { id: 't12', date: 'Jun 15', dateISO: '2026-06-15', type: 'out', accountId: 'payroll', desc: 'Vendor payment — Helios Corp', wallet: 'Payroll', ccy: 'USDT', amount: -8500, hash: '3a90e7f56cd1' },
    { id: 't13', date: 'Apr 10', dateISO: '2026-04-10', type: 'out', accountId: 'ops', desc: 'Vendor payment — Legacy Partners', wallet: 'Ops', ccy: 'USDC', amount: -6300, hash: '7d13ffa209ab' },
    { id: 't14', date: 'May 1, 2025', dateISO: '2025-05-01', type: 'in', accountId: 'payroll', desc: 'Client deposit — Founding round', wallet: 'Payroll', ccy: 'USDT', amount: 75000, hash: '2c88bb410e5f' }
  ],
  // ccy/chain seed from the "default stablecoin/chain" Settings preference
  // rather than the reporting currency — Receive has no address to show
  // (and greets the user with an empty state) until a real stablecoin is
  // selected, so defaulting to one instead of the fiat reporting currency
  // means a fresh Receive visit is immediately actionable.
  receive: { accountId: 'ops', ccy: initialSettings.defaultStablecoin, chain: initialSettings.defaultChain, addressLoading: false, addressError: null, showDetails: false },
  move: { fromAccountId: 'ops', ccy: 'USDC', chain: NET.USDC, toKey: '', amount: '', memo: '', step: 'form', errors: {}, resultTx: null, sending: false },
  savedRecipients: [],
  txnFilter: 'all',
  txnAccountFilter: 'all',
  txnTimeFilter: 'all',
  expandedTxId: null,
  txnTags: loadTxnTags(), // { [txnId]: string[] }
  tagDraft: '', // free-text input for the currently-expanded row's new tag
  showAddRecipient: false, newRecipName: '', newRecipAddress: '', newRecipError: '',
  showOpenAccount: false, newAcctName: '', newAcctOwner: '', newAcctError: '',
  privateKeys: [], // synced from the worker: [{id,label,revealed,words?}]
  showImportKey: false, importText: '', importError: '',
  showConfirmReveal: false, confirmRevealKeyId: null, confirmRevealCode: '', confirmRevealError: '',
  lockCode: '', lockError: '',
  toast: null,
  online: typeof navigator !== 'undefined' ? navigator.onLine : true,
  workerConnected: null, // null = not checked yet, true/false once known
  showStatusPanel: false,
  showLicense: false,
  showProfileMenu: false,
  homeEdit: { accountId: null, field: null, draft: '' }, // field: 'name' | 'owner'
  // Cache of derived addresses for Home's per-balance explorer-link icon,
  // keyed by "accountId|ccy" — populated once at boot (see
  // ensureHomeAddresses/main.js), not per-render (a render function must
  // stay synchronous; see app.js's render() comment on reentrant renders).
  homeAddresses: {},
  showBalanceInfo: false,
  showActivityInfo: false,
  recipientEdit: null, // { id, name, address } while editing an existing recipient, else null
  profileEdit: { profileId: null, field: null, draft: '', error: '' }, // field: 'name' | 'email'
  showRecoveryInfo: false,
  swUpdateAvailable: false,
  copyFlash: null, // key of the just-copied button ('address' | 'link'), cleared after a short delay
  accountSort: 'default', // 'default' | 'name-asc' | 'name-desc' | 'balance-desc' | 'balance-asc'
  txnSort: 'date-desc',
  txnSearch: '',
  theme: initialTheme(),
  // Whether this device/browser has a platform authenticator at all —
  // checked once at boot (main.js) and cached here, since the check itself
  // is async. Combined with a profile's own `vault.webauthn` (does *this*
  // profile have a passkey enrolled) to decide whether Landing's unlock
  // screen offers a biometric option. Not a guarantee prf specifically is
  // supported — that's only known once registerWebauthn actually tries it.
  webauthnAvailable: false
};

// savedRecipients starts empty and is populated post-unlock (see
// loadRecipientsForSession in actions.js) — it's encrypted at rest under
// the active profile's vault key, which doesn't exist yet at module load.
export function getState() {
  return state;
}

export function setState(patch) {
  state = { ...state, ...(typeof patch === 'function' ? patch(state) : patch) };
  listeners.forEach((fn) => fn(state));
}

// Updates the store without notifying listeners — used for free-text input
// bindings so typing doesn't trigger a full re-render (which would tear
// down the focused <input> node on every keystroke). The value still
// persists in state for the next real render or validation pass.
export function setSilent(patch) {
  state = { ...state, ...(typeof patch === 'function' ? patch(state) : patch) };
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function fmtNum(n) {
  return (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function fmtReporting(n) {
  const code = getState().reportingCurrency;
  const symbol = CURRENCY_SYMBOLS[code];
  return symbol ? symbol + fmtNum(n) : fmtNum(n) + ' ' + code;
}

export function toReporting(ccy, amt) {
  return amt * (FX[ccy] || 1);
}

export function totalUSDFor(account) {
  return Object.entries(account.balances).reduce((t, [ccy, amt]) => t + toReporting(ccy, amt), 0);
}

const TXN_SORTS = {
  'date-desc': (a, b) => new Date(b.dateISO) - new Date(a.dateISO),
  'date-asc': (a, b) => new Date(a.dateISO) - new Date(b.dateISO),
  'amount-desc': (a, b) => Math.abs(b.amount) - Math.abs(a.amount),
  'amount-asc': (a, b) => Math.abs(a.amount) - Math.abs(b.amount)
};

// The exact filter/search/sort pipeline the Activity screen renders,
// factored out so CSV/PDF export ("the displayed activity rows") reuses
// this instead of a second copy that could quietly drift out of sync with
// what's actually on screen.
export function filteredTransactions(s) {
  const today = new Date();
  return s.transactions.filter((t) => {
    if (s.txnFilter === 'in' && !(t.amount > 0)) return false;
    if (s.txnFilter === 'out' && !(t.amount < 0)) return false;
    if (s.txnAccountFilter !== 'all' && t.accountId !== s.txnAccountFilter) return false;
    if (s.txnTimeFilter !== 'all') {
      const days = (today - new Date(t.dateISO + 'T00:00:00Z')) / 86400000;
      if (s.txnTimeFilter === '1m' && days > 31) return false;
      if (s.txnTimeFilter === '12m' && days > 366) return false;
    }
    return true;
  }).filter((t) => {
    const q = s.txnSearch.trim().toLowerCase();
    if (!q) return true;
    return (t.desc + ' ' + t.wallet + ' ' + t.hash).toLowerCase().includes(q);
  }).sort(TXN_SORTS[s.txnSort] || TXN_SORTS['date-desc']);
}

export function totalReportingAll() {
  return getState().wallets.reduce((t, w) => t + totalUSDFor(w), 0);
}

let toastTimer = null;
export function showToast(msg) {
  setState({ toast: msg });
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => setState({ toast: null }), 2200);
}
